import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { groupTradesByRegion } from './tradesAreasData';
import { META_TRADES_HUB, applyPageMeta } from './seoMeta';

const CANONICAL = META_TRADES_HUB.canonical;
const SCHEMA_ID = 'webcodecraft-trades-hub-schema';

function TradesHubPage() {
  const regions = useMemo(() => {
    const map = groupTradesByRegion();
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, []);

  const jsonLd = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${CANONICAL}#webpage`,
            url: CANONICAL,
            name: META_TRADES_HUB.title,
            description: META_TRADES_HUB.description,
            isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
            inLanguage: 'en-GB'
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: 'Trades & builders', item: CANONICAL }
            ]
          }
        ]
      }),
    []
  );

  useEffect(() => {
    const restoreMeta = applyPageMeta(META_TRADES_HUB);

    const existing = document.getElementById(SCHEMA_ID);
    if (!existing) {
      const el = document.createElement('script');
      el.id = SCHEMA_ID;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      restoreMeta();
      const script = document.getElementById(SCHEMA_ID);
      if (script) script.remove();
    };
  }, [jsonLd]);

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">Trades &amp; builders</span>
            </p>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Web design for tradespeople, builders &amp; contractors by city
            </h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-3xl">
              Pay monthly websites for plumbers, electricians, roofers, landscapers and construction firms across the South
              West, Dorset, Wiltshire, South Wales, and major UK cities including London, Manchester, Birmingham, Leeds,
              Scotland and Northern Ireland. Each city page explains SSL, hosting, technical SEO, and our
              no-large-upfront-build model.{' '}
              <a href={internalUrl('contact')} className="text-indigo-600 font-medium hover:underline">
                Start a project
              </a>{' '}
              or browse your area below.
            </p>

            {regions.map(([region, areas]) => (
              <section key={region} className="mb-12" aria-labelledby={`trades-hub-${areas[0].slug}`}>
                <h2 id={`trades-hub-${areas[0].slug}`} className="text-2xl font-bold text-gray-900 mb-4">
                  {region}
                </h2>
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 list-none p-0">
                  {areas.map((area) => (
                    <li key={area.slug}>
                      <Link
                        to={`/trades/${area.slug}`}
                        className="block rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors"
                      >
                        {area.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <p className="text-sm text-gray-500 mt-10">
              <Link to="/locations" className="text-indigo-600 hover:underline">
                UK locations hub
              </Link>
              {' · '}
              <a href={internalUrl('services')} className="text-indigo-600 hover:underline">
                Packages &amp; pricing
              </a>
              {' · '}
              <a href={internalUrl('faq')} className="text-indigo-600 hover:underline">
                FAQ
              </a>
            </p>
          </m.div>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default TradesHubPage;
